import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, Users, Shield, ShieldCheck, UserX, UserCheck, Settings, Trash2, Eye, Pencil, ChevronDown, ChevronRight, UserPlus, Info } from "lucide-react";
import { useFormValidation } from "../hooks/useFormValidation";
import { InputWithError } from "../components/common/InputWithError";
import { getPermissionSections, getDefaultUserPermissions, getAdminPermissions } from "../config/menuConfig";
import { useTranslation } from "../contexts/I18nContext";

// Usa la configurazione centralizzata per le sezioni permessi
const MENU_SECTIONS = getPermissionSections();

const getApiPrefix = () => {
  const currentTenant = localStorage.getItem('currentTenant');
  return currentTenant ? '/tenant' : '';
};

export default function GestioneUtentiPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionsDialog, setPermissionsDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [editForm, setEditForm] = useState({ nome: "", cognome: "", email: "", ruolo: "", stato: "", password: "" });
  const [permissions, setPermissions] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set(MENU_SECTIONS.map(s => s.group)));
  
  // Stato per creare nuovo utente
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    password: "",
    ruolo: "user"
  });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingPermissions, setUpdatingPermissions] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Validazione form creazione utente
  const { errors: createErrors, validate: validateCreate, clearErrors: clearCreateErrors } = useFormValidation({
    nome: { required: true },
    email: { required: true, email: true },
    password: { required: true, minLength: 6 }
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [usersRes, statsRes] = await Promise.all([
        api.get(`${prefix}/users`),
        api.get(`${prefix}/users/stats/summary`)
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        handleApiError(err, t("errors.unauthorizedAccess"));
      } else {
        handleApiError(err, t("errors.loadUsers"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Crea nuovo utente
  const handleCreateUser = async () => {
    // Validazione client-side
    if (!validateCreate(createForm)) {
      return;
    }
    
    setCreating(true);
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/users`, createForm);
      showSuccess(t("success.userCreated"));
      setCreateDialog(false);
      setCreateForm({ nome: "", cognome: "", email: "", password: "", ruolo: "user" });
      clearCreateErrors();
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorCreateUser"));
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({ nome: user.nome, cognome: user.cognome, email: user.email, ruolo: user.ruolo, stato: user.stato, password: "" });
    setEditDialog(true);
  };

  const openPermissionsDialog = (user) => {
    setSelectedUser(user);
    // Inizializza permessi con struttura { key: { view: bool, edit: bool } }
    const userPerms = user.permissions || {};
    const initialPerms = {};
    MENU_SECTIONS.forEach(section => {
      section.items.forEach(item => {
        if (typeof userPerms[item.key] === 'object') {
          initialPerms[item.key] = userPerms[item.key];
        } else if (typeof userPerms[item.key] === 'boolean') {
          // Migra vecchio formato
          initialPerms[item.key] = { view: userPerms[item.key], edit: userPerms[item.key] };
        } else {
          initialPerms[item.key] = { view: false, edit: false };
        }
      });
    });
    setPermissions(initialPerms);
    setPermissionsDialog(true);
  };

  const handleUpdateUser = async () => {
    setUpdating(true);
    try {
      const prefix = getApiPrefix();
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.put(`${prefix}/users/${selectedUser.id}`, payload);
      showSuccess(t("success.userUpdated"));
      setEditDialog(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.updateFailed"));
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePermissions = async () => {
    setUpdatingPermissions(true);
    try {
      const prefix = getApiPrefix();
      await api.put(`${prefix}/users/${selectedUser.id}/permissions`, { permissions });
      showSuccess(t("success.permissionsUpdated"));
      setPermissionsDialog(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorUpdatePermissions"));
    } finally {
      setUpdatingPermissions(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeleting(true);
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/users/${deleteDialog.user.id}`);
      showSuccess(t("success.userDeleted"));
      setDeleteDialog({ open: false, user: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    const prefix = getApiPrefix();
    for (const id of ids) {
      try {
        await api.delete(`${prefix}/users/${id}`);
        deleted++;
      } catch { /* ignore */ }
    }
    if (deleted > 0) showSuccess(`${deleted} utenti eliminati`);
    fetchData();
  };

  const toggleGroup = (group) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(group)) newSet.delete(group);
    else newSet.add(group);
    setExpandedGroups(newSet);
  };

  const toggleAllView = (section, value) => {
    const newPerms = { ...permissions };
    section.items.forEach(item => {
      newPerms[item.key] = { ...newPerms[item.key], view: value };
      if (!value) newPerms[item.key].edit = false; // Se toglie view, toglie anche edit
    });
    setPermissions(newPerms);
  };

  const toggleAllEdit = (section, value) => {
    const newPerms = { ...permissions };
    section.items.forEach(item => {
      if (value) newPerms[item.key] = { ...newPerms[item.key], view: true, edit: true };
      else newPerms[item.key] = { ...newPerms[item.key], edit: false };
    });
    setPermissions(newPerms);
  };

  const getRuoloBadge = (ruolo) => {
    if (ruolo === "admin") {
      return <Badge className="bg-purple-100 text-purple-700">Admin</Badge>;
    }
    return <Badge variant="outline">{t('common.user')}</Badge>;
  };

  const getStatoBadge = (stato) => {
    const config = {
      attivo: { className: "bg-green-100 text-green-700", label: "Attivo" },
      disattivo: { className: "bg-red-100 text-red-700", label: "Disattivo" },
      in_attesa: { className: "bg-yellow-100 text-yellow-700", label: "In Attesa" }
    };
    const c = config[stato] || { className: "bg-gray-100", label: stato };
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
      });
    } catch { return dateStr; }
  };

  const getPermissionsSummary = (user) => {
    if (user.ruolo === "admin") return <Badge className="bg-purple-100 text-purple-700">Accesso Completo</Badge>;
    const perms = user.permissions || {};
    let viewCount = 0, editCount = 0;
    Object.values(perms).forEach(p => {
      if (typeof p === 'object') {
        if (p.view) viewCount++;
        if (p.edit) editCount++;
      } else if (p) {
        viewCount++;
        editCount++;
      }
    });
    if (viewCount === 0) return <Badge variant="outline" className="text-red-600">Nessun accesso</Badge>;
    return (
      <div className="flex gap-1">
        <Badge variant="outline" className="text-blue-600"><Eye className="h-3 w-3 mr-1" />{viewCount}</Badge>
        <Badge variant="outline" className="text-amber-600"><Pencil className="h-3 w-3 mr-1" />{editCount}</Badge>
      </div>
    );
  };

  const columns = [
    {
      key: 'nome_completo',
      label: t('common.name'),
      getValue: (row) => `${row.nome || ''} ${row.cognome || ''}`.trim(),
      render: (value) => <span className="font-medium">{value}</span>
    },
    { key: 'email', label: 'Email' },
    { key: 'ruolo', label: 'Ruolo', render: (value) => getRuoloBadge(value) },
    { key: 'stato', label: t('common.status'), render: (value) => getStatoBadge(value) },
    { 
      key: 'permissions_summary', 
      label: 'Permessi', 
      render: (_, row) => getPermissionsSummary(row)
    },
    {
      key: 'ultimo_accesso',
      label: 'Ultimo Accesso',
      render: (value) => <span className="text-sm text-muted-foreground">{formatDate(value)}</span>
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      render: (_, user) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openPermissionsDialog(user)} title="Gestisci permessi">
            <Settings className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} title="Modifica ruolo/stato">
            <Shield className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, user })} className="text-destructive" title={t("common.delete")}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestione-utenti-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("users.title")}</h1>
          <p className="text-muted-foreground text-sm">Gestisci accessi e permessi granulari per ogni sezione</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="btn-nuovo-utente">
          <UserPlus className="h-4 w-4 mr-2" />
          Nuovo Utente
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t("common.total")}</p>
                  <p className="text-2xl font-bold">{stats.totale}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <UserCheck className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('common.activePlural')}</p>
                  <p className="text-2xl font-bold text-green-600">{stats.attivi}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <UserX className="h-8 w-8 text-red-500" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Disattivi</p>
                  <p className="text-2xl font-bold text-red-600">{stats.disattivi}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('common.scheduled')}</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.in_attesa}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Admin</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.amministratori}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Utenti ({users.length})</CardTitle>
          <CardDescription>Clicca su ⚙️ per configurare i permessi view/edit per ogni sezione</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={users}
            onDelete={handleBulkDelete}
            idField="id"
            emptyMessage="Nessun utente trovato"
          />
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("users.editUser")}</DialogTitle>
            <DialogDescription>
              {selectedUser?.nome} {selectedUser?.cognome} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.name')}</Label>
                <Input
                  value={editForm.nome}
                  onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                  data-testid="edit-user-nome"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('auth.lastName')}</Label>
                <Input
                  value={editForm.cognome}
                  onChange={(e) => setEditForm({ ...editForm, cognome: e.target.value })}
                  data-testid="edit-user-cognome"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('auth.email')}</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Reset Password</Label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Lascia vuoto per non cambiare"
                data-testid="edit-user-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select value={editForm.ruolo} onValueChange={(v) => setEditForm({ ...editForm, ruolo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Amministratore</SelectItem>
                    <SelectItem value="user">Utente Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Stato Account</Label>
                <Select value={editForm.stato} onValueChange={(v) => setEditForm({ ...editForm, stato: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attivo">{t('common.active')}</SelectItem>
                    <SelectItem value="disattivo">Disattivo</SelectItem>
                    <SelectItem value="in_attesa">{t('common.scheduled')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} disabled={updating}>{t("common.cancel")}</Button>
            <Button onClick={handleUpdateUser} disabled={updating} data-testid="save-edit-user-btn">
              {updating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog - Granulare */}
      <Dialog open={permissionsDialog} onOpenChange={setPermissionsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Permessi Utente
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.nome} {selectedUser?.cognome} - {selectedUser?.ruolo === "admin" ? "Amministratore" : "Utente Standard"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser?.ruolo === "admin" ? (
            <div className="py-8 text-center">
              <ShieldCheck className="h-16 w-16 text-purple-500 mx-auto mb-4" />
              <p className="text-lg font-medium">Accesso Completo</p>
              <p className="text-muted-foreground">Gli amministratori hanno accesso a tutte le sezioni.</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-4">
                {MENU_SECTIONS.map((section) => {
                  const isExpanded = expandedGroups.has(section.group);
                  const allView = section.items.every(item => permissions[item.key]?.view);
                  const allEdit = section.items.every(item => permissions[item.key]?.edit);
                  
                  return (
                    <div key={section.group} className="border rounded-lg overflow-hidden">
                      {/* Group Header */}
                      <div 
                        className="flex items-center justify-between p-3 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                        onClick={() => toggleGroup(section.group)}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          <span className="font-semibold">{section.group}</span>
                          <Badge variant="outline" className="text-xs">{section.items.length}</Badge>
                        </div>
                        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5">
                            <Eye className="h-4 w-4 text-blue-500" />
                            <Checkbox 
                              checked={allView}
                              onCheckedChange={(v) => toggleAllView(section, v)}
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Pencil className="h-4 w-4 text-amber-500" />
                            <Checkbox 
                              checked={allEdit}
                              onCheckedChange={(v) => toggleAllEdit(section, v)}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Group Items */}
                      {isExpanded && (
                        <div className="divide-y">
                          {section.items.map((item) => (
                            <div key={item.key} className="flex items-center justify-between p-3 pl-8 hover:bg-muted/30">
                              <div>
                                <p className="font-medium text-sm">{item.label}</p>
                                <p className="text-xs text-muted-foreground">{item.description}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-xs text-blue-600">View</Label>
                                  <Checkbox
                                    checked={permissions[item.key]?.view || false}
                                    onCheckedChange={(v) => {
                                      const newPerms = { ...permissions };
                                      newPerms[item.key] = { 
                                        ...newPerms[item.key], 
                                        view: v,
                                        edit: v ? newPerms[item.key]?.edit : false 
                                      };
                                      setPermissions(newPerms);
                                    }}
                                  />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-xs text-amber-600">Edit</Label>
                                  <Checkbox
                                    checked={permissions[item.key]?.edit || false}
                                    disabled={!permissions[item.key]?.view}
                                    onCheckedChange={(v) => {
                                      const newPerms = { ...permissions };
                                      newPerms[item.key] = { ...newPerms[item.key], edit: v };
                                      setPermissions(newPerms);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsDialog(false)} disabled={updatingPermissions}>{t("common.cancel")}</Button>
            {selectedUser?.ruolo !== "admin" && (
              <Button onClick={handleUpdatePermissions} disabled={updatingPermissions}>
                {updatingPermissions ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : t("common.save")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Eliminare l&apos;utente {deleteDialog.user?.nome} {deleteDialog.user?.cognome}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })} disabled={deleting}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deleting}>
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onOpenChange={(open) => { setCreateDialog(open); if (!open) clearCreateErrors(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("users.newUser")}</DialogTitle>
            <DialogDescription>
              Crea un nuovo account utente per questo tenant
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <InputWithError
                  id="nome"
                  value={createForm.nome}
                  onChange={(e) => setCreateForm({ ...createForm, nome: e.target.value })}
                  placeholder="Mario"
                  error={createErrors.nome}
                  data-testid="input-nome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">{t('auth.lastName')}</Label>
                <Input
                  id="cognome"
                  value={createForm.cognome}
                  onChange={(e) => setCreateForm({ ...createForm, cognome: e.target.value })}
                  placeholder="Rossi"
                  data-testid="input-cognome"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <InputWithError
                id="email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="mario.rossi@esempio.it"
                error={createErrors.email}
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <InputWithError
                id="password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Minimo 6 caratteri"
                error={createErrors.password}
                data-testid="input-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ruolo">Ruolo</Label>
              <Select value={createForm.ruolo} onValueChange={(v) => setCreateForm({ ...createForm, ruolo: v })}>
                <SelectTrigger data-testid="select-ruolo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Utente Standard</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)} disabled={creating}>{t("common.cancel")}</Button>
            <Button onClick={handleCreateUser} disabled={creating} data-testid="btn-salva-utente">
              {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('dialogs.creating')}</> : "Crea Utente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
